// EVERY call. One wrapper around globalThis.fetch, installed once at boot.
//
// A 401 never means "you may not do that" — that's 403. It means the backend saw no
// session: it expired, it was revoked, or the backend was restarted against a different
// database. The console's auth gate only checks the session at mount, so from then on a
// dead session looks like a working console whose every action mysteriously fails
// ("Could not create user"), with the operator's email still sitting in the top bar.
//
// Why a fetch wrapper rather than hooks in each client: better-fetch resolves the fetch
// implementation PER REQUEST (`const fetch = getFetch(opts)` inside betterFetch, and
// getFetch reads globalThis.fetch), so one wrapper covers better-auth's client, every
// helper in backOfficeApi, and any raw fetch() anywhere else — including ones not written
// yet. Per-client hooks covered only the clients someone remembered to wire.
//
// No pub-sub, no useEffect: the fetch wrapper pushes straight onto the existing toast bus.
import { toast, TOAST_BUS } from './buses';
import { getBackendUrl } from './backendUrl';
import { passkeySupport, readPasskeyHint, signInWithPasskey } from './passkey';
import { clearSessionMarker } from './sessionMarker';

let announced = false;

// A 401 from these is a bad credential, not a lost session — telling someone at the
// sign-in screen that their session expired is nonsense.
const isCredentialCheck = (url) => /\/sign-in|\/sign-up|\/sign-out|\/email-otp|\/get-session|\/forget-password|\/reset-password/.test(url);

const signOutAndReload = async () => {
  // Forget the optimistic-boot marker FIRST, so the reload below lands on the sign-in gate
  // rather than re-entering the shell of a session we already know is dead. Done before the
  // network call, because it must happen whether or not that call succeeds.
  clearSessionMarker();
  try {
    await fetch(`${getBackendUrl()}/api/auth/sign-out`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
  } catch { /* the cookie is already dead; reloading still lands on the gate */ }
  window.location.reload();
};

// A toast, not a takeover: it states the fact and offers the one move, without seizing the
// screen or discarding whatever the operator had half-typed. It doesn't auto-dismiss (see
// Toast.jsx) — an actionable toast that vanishes in 4s is worse than none.
export const handleUnauthorized = (url = '') => {
  if (announced || isCredentialCheck(url)) return;
  announced = true;
  toast('Session expired', 'error', 'Your session has ended — sign in again to continue.', {
    label: 'Log out',
    onClick: signOutAndReload,
  });
  // AFTER the toast above (itself kind 'error'): from here on every failing call is this
  // same dead session reported again.
  TOAST_BUS.suppressErrors = true;
};

// ─────────────────────────────────────────────────────────────────────────────
// SILENT RE-AUTHENTICATION
//
// The property this establishes: THE SESSION IS DISPOSABLE, THE CREDENTIAL IS
// DURABLE. A session ending is a normal event, not an incident — so the console
// re-asserts who you are and carries on, and the operator never sees a gate.
//
// This is deliberately the alternative to the patches this problem attracts:
// lengthening the session TTL, adding a "remember me", minting refresh tokens,
// keeping a heartbeat alive. Every one of those makes the session live LONGER —
// which is a security cost paid to avoid a UX cost. Making re-auth free removes
// the reason to want a long session at all, so the expiry can stay short.
//
// Three invariants, each of which is load-bearing:
//
//  1. SINGLE FLIGHT. A dashboard fires many requests at once; without this,
//     eight parallel 401s mean eight Touch ID prompts stacked on each other,
//     which is far worse than the problem being solved. All callers await ONE
//     ceremony and share its outcome.
//  2. AT MOST ONE RETRY. The replayed request is marked, so if it 401s again we
//     fall through to the gate instead of looping a biometric prompt forever.
//  3. ONLY WHEN IT CAN SUCCEED SILENTLY. No enrolled credential on this machine
//     (no hint), or no platform authenticator, means we do not interrupt with a
//     ceremony that is going to fail — we go straight to the honest toast.
// ─────────────────────────────────────────────────────────────────────────────

let reauthInFlight = null;

const canReauth = async () => {
  if (!readPasskeyHint()) return false;          // nothing enrolled here
  const s = await passkeySupport().catch(() => null);
  return !!(s && s.supported && s.platform);
};

/** One ceremony, shared by every caller that raced into it. */
const reauthenticate = () => {
  if (!reauthInFlight) {
    reauthInFlight = (async () => {
      try {
        await signInWithPasskey();
        return true;
      } catch {
        // Cancelled or failed — the caller falls back to the gate. Not a place
        // to retry: the operator just told us no, or the credential is gone.
        return false;
      } finally {
        // Cleared on the next tick so requests that 401 microseconds apart still
        // join THIS attempt rather than starting a second one.
        setTimeout(() => { reauthInFlight = null; }, 0);
      }
    })();
  }
  return reauthInFlight;
};

// Wrap once. Idempotent so an HMR re-evaluation can't stack wrappers.
export const installSessionGuard = () => {
  if (typeof globalThis === 'undefined' || globalThis.__sessionGuardInstalled) return;
  globalThis.__sessionGuardInstalled = true;
  const base = getBackendUrl();
  const realFetch = globalThis.fetch.bind(globalThis);
  // IS THE SESSION ACTUALLY GONE? Some routes answer "you may not" with 401 instead of 403, and reading every 401 as a
  // dead session told a signed-in operator "Session expired", started a passkey ceremony, and silenced every error
  // toast after it. The auth server is asked once (requests that 401 together share the one answer).
  let aliveCheck = null;
  const sessionAlive = () => {
    if (!aliveCheck) {
      aliveCheck = realFetch(`${base}/api/auth/get-session`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json().catch(() => null) : null))
        .then((j) => Boolean(j?.session || j?.user))
        .catch(() => false)
        .finally(() => { setTimeout(() => { aliveCheck = null; }, 2000); });
    }
    return aliveCheck;
  };
  globalThis.fetch = async (input, init) => {
    const response = await realFetch(input, init);
    try {
      if (response.status === 401) {
        const url = typeof input === 'string' ? input : (input?.url || response.url || '');
        const ours = String(url).startsWith(base) || String(response.url || '').startsWith(base);
        // Still signed in: a refusal, not an expiry — the caller shows its own error.
        if (ours && !isCredentialCheck(String(url)) && await sessionAlive()) return response;
        if (ours && !isCredentialCheck(String(url)) && !init?.__reauthRetry) {
          if (await canReauth()) {
            if (await reauthenticate()) {
              // Signed in again. Replay the request the operator actually made,
              // marked so a second 401 cannot start another ceremony, and hand
              // back its response — the calling code never learns any of this
              // happened, which is the entire point.
              return realFetch(input, { ...(init || {}), __reauthRetry: true });
            }
          }
        }
        // Only OUR backend's 401s mean "your session died" — a third party's doesn't.
        if (ours) handleUnauthorized(String(url || response.url || ''));
      }
    } catch { /* never let the guard break a real request */ }
    return response;
  };
};
